import { useContext, useMemo } from "react";
import { ToastContext } from "../contexts/ToastContext";

/**
 * useToast hook - Provides convenient methods to show toast notifications
 *
 * Usage:
 *   const { toast } = useToast();
 *   toast.success("Note updated successfully");
 *   toast.error("Failed to update note");
 *   toast.error("Failed to save", { onRetry: () => handleSave() });
 *   toast.critical("Account deletion failed"); // Red for critical
 *
 * @returns {object} - { toast: { success, error, warning, critical, info }, removeToast, clearToasts }
 */
export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }

  const { addToast, removeToast, clearToasts } = context;

  // Create convenience methods for each variant
  const toast = useMemo(
    () => ({
      success: (message, options = {}) => {
        const duration =
          typeof options === "number" ? options : (options.duration ?? 3000);
        const opts = typeof options === "number" ? {} : options;
        return addToast(message, "success", duration, opts);
      },
      error: (message, options = {}) => {
        const duration =
          typeof options === "number" ? options : (options.duration ?? 4000);
        const opts = typeof options === "number" ? {} : options;
        return addToast(message, "error", duration, opts);
      },
      warning: (message, options = {}) => {
        const duration =
          typeof options === "number" ? options : (options.duration ?? 4000);
        const opts = typeof options === "number" ? {} : options;
        return addToast(message, "warning", duration, opts);
      },
      critical: (message, options = {}) => {
        const duration =
          typeof options === "number" ? options : (options.duration ?? 5000);
        const opts = typeof options === "number" ? {} : options;
        return addToast(message, "critical", duration, opts);
      },
      info: (message, options = {}) => {
        const duration =
          typeof options === "number" ? options : (options.duration ?? 3000);
        const opts = typeof options === "number" ? {} : options;
        return addToast(message, "info", duration, opts);
      },
    }),
    [addToast],
  );

  return { toast, removeToast, clearToasts };
}
